import React from 'react';
import PropTypes from 'prop-types';
import PopOver from '@prisjakt/ui-popover';
import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';

import ExpandedInfo from './components/expanded-info';
import ExpandButton from './components/expand-button';
import StoreInfoColumn from './components/store-info-column';
import StockStatusColumn from './components/stock-status-column';
import PriceColumn from './components/price-column';
import GoToStoreButton from './components/go-to-store-button';
import ReportButton from './components/report-button';
import Viewport from '../viewport';

import * as S from './styles';
import * as Table from './styles/table';

export const columnCount = 6;

class PriceRow extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      showDisabledInfo: false,
      showReportOverlay: false,
    };
    this.priceRow = React.createRef();
    this.clickEvent = e => this.handleOutSideClick(e);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.clickEvent);
    document.removeEventListener('visibilitychange', this.onRefocus);
  }

  handleOutSideClick(e) {
    if (
      this.priceRow &&
      this.priceRow.current &&
      this.priceRow.current.contains(e.target)
    ) {
      return;
    }
    window.removeEventListener('click', this.clickEvent);
    this.setState({ showDisabledInfo: false });
  }

  toggleDisabledInfo = () => {
    this.setState(
      ({ showDisabledInfo }) => ({ showDisabledInfo: !showDisabledInfo }),
      () => {
        if (this.state.showDisabledInfo) {
          window.addEventListener('click', this.clickEvent);
        } else {
          window.removeEventListener('click', this.clickEvent);
        }
      }
    );
  };

  toggleExpanded = () =>
    this.setState(state => ({ expanded: !state.expanded }));

  handleClickOut = e => {
    const {
      isInternationalPrice,
      externalUri,
      onPriceClick,
      store,
    } = this.props;
    const { showDisabledInfo } = this.state;
    if (externalUri) {
      if (IS_BROWSER) {
        this.startBounceTimer();
      }
      if (!showDisabledInfo && !e.defaultPrevented) {
        onPriceClick(
          store && store.featured,
          undefined,
          isInternationalPrice ? 'international' : undefined
        );
      }
    } else {
      e.preventDefault();
      this.toggleDisabledInfo();
    }
  };

  handleClickOutAltPrice = () => {
    const { isInternationalPrice, onPriceClick, store } = this.props;
    onPriceClick(
      store && store.featured,
      undefined,
      isInternationalPrice ? 'international' : undefined
    );
  };

  startBounceTimer() {
    // Start a timer when a user clicks out on a product
    // if they return to Prisjakt within 15 seconds
    // display an error report button in the price row.

    const startTime = Date.now();

    this.onRefocus = () => {
      if (
        Date.now() - startTime > 15000 &&
        document.visibilityState === 'visible'
      ) {
        document.removeEventListener('visibilitychange', this.onRefocus);
        return;
      }

      if (document.visibilityState === 'visible') {
        this.setState({
          showReportOverlay: true,
        });

        // Action event for tracking
        this.props.onClickOutBounce(this.props.productId, this.props.priceId);

        document.removeEventListener('visibilitychange', this.onRefocus);
      }
    };

    document.addEventListener('visibilitychange', this.onRefocus);
  }

  closeReportOverlay = () => {
    this.setState({ showReportOverlay: false });
  };

  render() {
    const {
      externalUri,
      isInternationalPrice,
      openInNewWindow,
      brandRecommendedRetailer,
      brand,
      store,
      storeInfo,
      alternativePrices,
      storeBadges,
      priceId,
      priceType,
      price,
      campaignStyling,
      stock,
      showStoreProductNames,
    } = this.props;

    const { t } = this.context;
    const { expanded, showDisabledInfo, showReportOverlay } = this.state;
    const featured = store.featured;
    const tableLinkProps = {
      rel: 'nofollow noopener',
      href: externalUri,
      onClick: this.handleClickOut,
      target: openInNewWindow ? '_blank' : '',
      disabled: !externalUri,
    };

    return (
      <>
        <Table.Row
          // Classname is used to track click out
          className="pj-ui-price-row"
          nonFeatured={!featured}
          noExternalUri={!externalUri}
          nonFeaturedWithExternalUri={!featured && externalUri}
          data-test="PriceRow"
          ref={this.priceRow}
        >
          <Table.Column>
            <ExpandButton
              title={t('product', 'price.more-info')}
              onClick={this.toggleExpanded}
              expanded={expanded}
            />
          </Table.Column>
          <Table.Column>
            <Table.Link {...tableLinkProps} alignLeft>
              <StoreInfoColumn
                store={store}
                userReviewSummary={store.userReviewSummary}
                brandRecommendedRetailer={false}
              />
            </Table.Link>
          </Table.Column>
          <Table.Column expand>
            <Table.Link {...tableLinkProps} alignLeft>
              <S.ProductNameColumn>
                <Viewport desktop>
                  {IS_BROWSER && showReportOverlay ? (
                    <ReportButton
                      storeId={store.id}
                      priceId={priceId}
                      priceValue={price.exclShipping}
                      stockStatus={stock.status}
                      onClose={this.closeReportOverlay}
                    />
                  ) : (
                    <Viewport desktop>
                      <S.ProductName>
                        {showStoreProductNames &&
                        alternativePrices &&
                        alternativePrices.length
                          ? alternativePrices[0].name
                          : ''}
                      </S.ProductName>
                    </Viewport>
                  )}

                  {brandRecommendedRetailer && (
                    <S.RecommendedRetailer>
                      <S.RecommendedRetailerIcon />
                      <T domain="store">brand.recommended-retailer</T>
                    </S.RecommendedRetailer>
                  )}
                </Viewport>
              </S.ProductNameColumn>
            </Table.Link>
          </Table.Column>
          <Table.Column>
            <Table.Link {...tableLinkProps} alignRight>
              <StockStatusColumn
                status={stock.status}
                statusText={stock.statusText}
                storeMarket={store.market}
                countryCode={store.countryCode}
                toneDown={!store.featured}
                isInternationalPrice={isInternationalPrice}
                showTooltip
                priceType={priceType}
              />
            </Table.Link>
          </Table.Column>
          <Table.Column>
            <Table.Link {...tableLinkProps} alignRight>
              <PriceColumn
                price={price}
                storeIsFeatured={store.featured}
                campaignStyling={campaignStyling}
                isInternationalPrice={isInternationalPrice}
                align="right"
              />
            </Table.Link>
          </Table.Column>
          <Table.Column>
            {externalUri ? (
              <Table.Link {...tableLinkProps}>
                <GoToStoreButton
                  visible
                  noPadding
                  disabled={!featured}
                  featured={featured}
                />
              </Table.Link>
            ) : (
              <PopOver
                id={`click-out-${priceId}`}
                open={Boolean(showDisabledInfo)}
                width={250}
                offsetY={-35}
                offsetX={40}
                arrowOffsetY={35}
                position="left"
                label={t('product', 'transparency.prices-more-info-button')}
                onClose={this.toggleDisabledInfo}
              >
                <Table.Link {...tableLinkProps}>
                  <GoToStoreButton
                    visible={Boolean(externalUri || featured)}
                    disabled={Boolean(externalUri && !featured)}
                    featured={featured}
                  />
                </Table.Link>
                <S.TransparencyShopInfo>
                  <T domain="product">transparency.store-info</T>
                </S.TransparencyShopInfo>
              </PopOver>
            )}
          </Table.Column>
        </Table.Row>

        {expanded && (
          <Table.InfoRow data-test="ColumnExpanded">
            <td colSpan="6">
              <ExpandedInfo
                brand={brand}
                brandRecommendedRetailer={brandRecommendedRetailer}
                storeName={store.name}
                storeLink={store.pathName}
                storeInfo={storeInfo}
                storeBadges={storeBadges}
                price={price}
                priceType={priceType}
              />
            </td>
          </Table.InfoRow>
        )}
        {expanded &&
          alternativePrices.map(altPrice => (
            <Table.AltRow key={altPrice.id}>
              <Table.Column colSpan="3" expandOnMobile>
                <Table.Link
                  {...tableLinkProps}
                  onClick={this.handleClickOutAltPrice}
                  href={altPrice.externalUri}
                  vertical
                  alignLeft
                >
                  <div>
                    <S.ProductName>{altPrice.name}</S.ProductName>
                    <S.StockStatus>
                      {altPrice.stock.statusText
                        ? altPrice.stock.statusText.replace(/-/g, '\u2011')
                        : t(
                            'product',
                            `stock-status.${altPrice.stock.status || 'unknown'}`
                          )}
                    </S.StockStatus>
                  </div>
                </Table.Link>
              </Table.Column>
              <Table.Column>
                <Table.Link
                  {...tableLinkProps}
                  onClick={this.handleClickOutAltPrice}
                  href={altPrice.externalUri}
                  alignRight
                >
                  <StockStatusColumn
                    status={altPrice.stock.status}
                    statusText={altPrice.stock.statusText}
                    storeMarket={store.market}
                    countryCode={store.countryCode}
                    toneDown={!store.featured}
                    isInternationalPrice={isInternationalPrice}
                  />
                </Table.Link>
              </Table.Column>
              <Table.Column>
                <Table.Link
                  {...tableLinkProps}
                  onClick={this.handleClickOutAltPrice}
                  href={altPrice.externalUri}
                  alignRight
                >
                  <PriceColumn
                    price={altPrice.price}
                    storeIsFeatured={store.featured}
                    campaignStyling={campaignStyling}
                    isInternationalPrice={isInternationalPrice}
                    align="right"
                  />
                </Table.Link>
              </Table.Column>
              <Table.Column>
                <Table.Link
                  {...tableLinkProps}
                  onClick={this.handleClickOutAltPrice}
                  href={altPrice.externalUri}
                  alignRight
                >
                  <GoToStoreButton
                    visible={Boolean(externalUri || featured)}
                    disabled={Boolean(externalUri && !featured)}
                    featured={featured}
                  />
                </Table.Link>
              </Table.Column>
            </Table.AltRow>
          ))}
      </>
    );
  }
}

const {
  string,
  number,
  bool,
  arrayOf,
  oneOf,
  oneOfType,
  shape,
  func,
} = PropTypes;

PriceRow.contextType = AppContext;

PriceRow.propTypes = {
  store: shape({
    id: number,
    name: string,
    featured: bool,
    hasLogo: bool,
    pathName: string,
    market: string,
    countryCode: string,
    primaryMarket: string,
  }).isRequired,
  storeInfo: arrayOf(string),
  storeBadges: arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired,
      sentiment: oneOf(['positive', 'negative']),
      nrOfPositiveReviews: number.isRequired,
      nrOfReviews: number.isRequired,
    })
  ),
  priceId: oneOfType([string, number]).isRequired,
  productId: oneOfType([string, number]).isRequired,
  priceType: string,
  price: shape({
    inclShipping: number,
    exclShipping: number,
    originalCurrency: string,
    originalCurrencyExclShipping: string,
  }).isRequired,
  campaignStyling: string,
  stock: shape({
    status: string,
    statusText: string,
  }),
  externalUri: string,
  knownShippingPrice: bool.isRequired,
  isInternationalPrice: bool,
  brandRecommendedRetailer: bool,
  brand: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    logo: PropTypes.string,
  }),
  onPriceClick: func,
  alternativePrices: arrayOf(
    shape({
      id: string.isRequired,
      name: string,
      type: string,
      externalUri: string,
      stock: shape({
        status: string,
        statusText: string,
      }),
      price: shape({
        inclShipping: number,
        exclShipping: number,
        originalCurrency: string,
        originalCurrencyExclShipping: string,
      }),
    })
  ),
  openInNewWindow: bool.isRequired,
  onClickOutBounce: func.isRequired,
  showStoreProductNames: bool,
};

PriceRow.defaultProps = {
  onPriceClick: () => {},
  alternativePrices: [],
  externalUri: undefined,
  storeInfo: null,
  brand: null,
  isInternationalPrice: false,
  brandRecommendedRetailer: false,
  storeBadges: [],
  priceType: undefined,
  campaignStyling: null,
  stock: undefined,
  showStoreProductNames: true,
};

export default PriceRow;
